'use client';

import { Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import { type ReactElement, useMemo } from 'react';

import { LoginEvents } from '@libs/analytics';
import {
    AccountDrawerType,
    cartHasVetDietItem,
    cartHasVetRxItem,
    PageType,
    useCart,
    useOptimizely,
    usePageLayout,
    useUserSession,
} from '@libs/common-next';
import type { LoyaltySignIn } from '@libs/contentful';

import { InterstitialSignIn } from '../InterstitialSignIn';
import { generateVelocityCreateAccountUrl } from './generateCreateAccountUrl';
import './SignInDrawer.scss';

export function SignInDrawer({
    data,
}: Readonly<{
    data?: LoyaltySignIn;
}>): ReactElement | null {
    const componentClassName = 'sign-in-drawer';

    const { cart } = useCart();
    const { isSoftAuthenticated } = useUserSession();
    const { context } = useOptimizely();

    const cartHasRx = cart && cartHasVetRxItem(cart);
    const cartHasVetDiet = cart && cartHasVetDietItem(cart);
    const showRxMessage = cartHasRx || cartHasVetDiet;

    const { accountDrawersOpen, removeAccountDrawers, signInDrawerProps, pageType } = usePageLayout();
    const createAccountHref = useMemo(() => {
        const { productToAddToWishlist } = signInDrawerProps ?? {};

        switch (pageType) {
            case PageType.PLP:
            case PageType.PDP:
                if (productToAddToWishlist) {
                    return generateVelocityCreateAccountUrl('wishlist', productToAddToWishlist as string);
                }
                return undefined;
            default:
                return undefined;
        }
    }, [pageType, signInDrawerProps]);

    const benefitItems = data?.items?.[0]?.fields?.items || [];
    const isActive = accountDrawersOpen?.includes(AccountDrawerType.SIGN_IN);

    return (
        <Drawer
            data-testid="sign-in-drawer"
            align="right"
            isActive={isActive}
            onClose={() => {
                removeAccountDrawers([AccountDrawerType.SIGN_IN]);
            }}
            ariaLabelledBy="drawer-heading-1"
            className={componentClassName}
        >
            <Drawer.Header>
                <Heading data-testid="sign-in-drawer-heading-1" id="drawer-heading-1" tagName="h4" size="title">
                    {(signInDrawerProps?.signInTitle as string) || data?.signInTitle}
                </Heading>
            </Drawer.Header>
            <Drawer.Body>
                {showRxMessage && (
                    <>
                        <TextPassage>
                            Pharmacy and Vet Diet orders require an account to checkout. Please sign in or create an
                            account to complete your purchase.
                        </TextPassage>
                        <br />
                    </>
                )}
                <div className={`${componentClassName}__body`}>
                    <InterstitialSignIn
                        itemsTitle={data?.title}
                        benefitItems={benefitItems}
                        isInDrawer
                        isSoftAuthenticatedServer={data?.isSoftAuthenticatedServer}
                        createAccountHref={createAccountHref}
                        location={
                            isSoftAuthenticated ? LoginEvents.REAUTHENTICATE_SIGN_IN_DRAWER : LoginEvents.SIGN_IN_DRAWER
                        }
                        onSuccessfulSignIn={() => {
                            context?.trackEvent('sign_ins');
                        }}
                        displayAutoshipDescription
                    />
                </div>
            </Drawer.Body>
        </Drawer>
    );
}
